/**
 * YouTube URL helpers. Resources store whatever link an admin pasted (youtu.be/<id>, watch?v=<id>&t=1s&pp=…, shorts/…, playlists…),
 * so the video id is always parsed out and the embed URL rebuilt — never string-replaced.
 */
const ID = /^[\w-]{11}$/;

export function youtubeId(url) {
  if (!url || typeof url !== "string") return null;
  try {
    const u = new URL(url.trim());
    const host = u.hostname.replace(/^(www|m)\./, "");
    let id = null;
    if (host === "youtu.be") id = u.pathname.split("/")[1];
    else if (/(^|\.)youtube(-nocookie)?\.com$/.test(host)) {
      if (u.pathname === "/watch") id = u.searchParams.get("v");
      else { const m = u.pathname.match(/^\/(?:embed|shorts|live|v)\/([^/?]+)/); id = m ? m[1] : null; }
    }
    return id && ID.test(id) ? id : null;
  } catch {
    return null;
  }
}

/** Privacy-enhanced embed; `rel=0` keeps the end screen to this channel's own videos. */
export const youtubeEmbedUrl = (id, { autoplay = true } = {}) =>
  `https://www.youtube-nocookie.com/embed/${id}?${new URLSearchParams({ autoplay: autoplay ? "1" : "0", rel: "0", modestbranding: "1", playsinline: "1" })}`;

export const youtubeWatchUrl = (id) => `https://www.youtube.com/watch?v=${id}`;

/**
 * The video's own thumbnail — the source of truth for every Video resource with a YouTube URL (see lib/videos.js), so an
 * admin never has to upload one. `hqdefault` is what every real YouTube upload has (never 404s); `youtubeMaxThumbnail`
 * is a higher-resolution rendition YouTube only generates for videos uploaded in HD, so callers try that FIRST and fall
 * back to this one (see components/resources/VideoThumb.jsx) rather than trust it blindly.
 */
export const youtubeThumbnail = (id) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

/** Higher-resolution rendition — only exists for videos uploaded in HD; the caller must fall back to `youtubeThumbnail` if this 404s. */
export const youtubeMaxThumbnail = (id) => `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`;
